import { IActivity } from '@sdk/contracts';
import { Button, Checkbox, Popover, Select } from 'antd';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Wrapper, ActionsWrapper, SelectWrapper } from './Actions.styles';

import { Icon } from '@/components/Icon';
import { useCourseDetail, useMoveActivity, useMutateSection } from '@/store';

type Props = {
  sectionId: string;
  activities: IActivity[];
};

export const Actions: FC<Props> = ({ sectionId, activities }) => {
  const { t } = useTranslation(['CourseTranslations', 'CoursesTranslations']);
  const { isWorking, moveActivityBetweenSections } = useMutateSection();
  const { course } = useCourseDetail();
  const [moveSection, setMoveSection] = useState<string>();
  const { data, setData } = useMoveActivity();

  const selectableActivities = useMemo(
    () => activities.filter(activity => !activity.parentActivity),
    [activities]
  );

  const isAllChecked = useMemo(
    () => selectableActivities.length > 0 && selectableActivities.every(m => data?.includes(m)),
    [data, selectableActivities]
  );
  const isMoveDisabled = useMemo(
    // TODO: Parent sections moving is temporary disabled on parent courses, enable them once design is ready
    () => !selectableActivities.some(m => data?.includes(m)) || course?.isParent,
    [data, selectableActivities, course?.isParent]
  );

  const onMove = () => {
    if (!course || !moveSection) return;
    const activitiesToMove = selectableActivities.filter(m => data?.includes(m));

    moveActivityBetweenSections(
      {
        originSection: sectionId,
        destinationSection: moveSection,
        activities: activitiesToMove
      },
      {
        onSuccess: () => {
          setData(data?.filter(d => !activitiesToMove.includes(d)));
          setMoveSection(undefined);
        },
        onError: error => {
          console.error('Error moving activities:', error);
          setData(data);
          setMoveSection(undefined);
        }
      }
    );
  };

  const handleCheckBox = () => {
    if (isAllChecked) {
      setData([]);
      return;
    }
    setData(selectableActivities);
  };

  const getFolderOptions = useMemo(() => {
    return course?.sections
      .filter(f => f._id !== sectionId)
      .map(f => ({ value: f._id, label: f.name }));
  }, [course?.sections, sectionId]);

  const handleChange = (f: string) => setMoveSection(f);

  if (selectableActivities.length === 0) return null;

  return (
    <Wrapper>
      <Checkbox checked={isAllChecked} onClick={handleCheckBox}>
        {t('CoursesTranslations:SELECT_ALL')}
      </Checkbox>
      <ActionsWrapper>
        <Popover
          content={
            <SelectWrapper>
              <Select
                style={{ width: '100%' }}
                disabled={isMoveDisabled}
                value={moveSection}
                onChange={handleChange}
                options={getFolderOptions}
              />
              <Button
                loading={isWorking}
                size="small"
                type="primary"
                onClick={onMove}
                disabled={!moveSection || isMoveDisabled}>
                {t('CourseTranslations:MOVE')}
              </Button>
            </SelectWrapper>
          }
          title={t('CourseTranslations:MOVE_TO_OTHER_SECTION')}
          trigger="click">
          <Button
            loading={isWorking}
            size="small"
            type="link"
            icon={<Icon i="FolderMoveTo" />}
            disabled={isMoveDisabled}
          />
        </Popover>
      </ActionsWrapper>
    </Wrapper>
  );
};
